import { FC, ReactNode } from "react";

import { AsyncData } from "@tsmono/util";

import styles from "./AsyncGate.module.css";
import { PulsingDots } from "./PulsingDots";

interface AsyncGateProps {
  /** The value to wait on; only its loading/error state is read here. */
  async: AsyncData<unknown>;
  /** Error message prefix, rendered as `{errorLabel}: {error.message}`. */
  errorLabel: string;
  loadingText: string;
  children: ReactNode;
}

/**
 * Gates a subtree on an `AsyncData` value: renders `children` once it has
 * resolved, a centered loading indicator while pending, or a centered error
 * message on failure. Callers that need the resolved value read it below the
 * gate via their own (now-guaranteed) accessor rather than receiving it here.
 */
export const AsyncGate: FC<AsyncGateProps> = ({
  async,
  errorLabel,
  loadingText,
  children,
}) => {
  if (async.error) {
    return (
      <div className={styles.gate}>
        {errorLabel}: {async.error.message}
      </div>
    );
  }
  if (async.loading) {
    return (
      <div className={styles.gate}>
        <PulsingDots size="large" text={loadingText} />
      </div>
    );
  }
  return <>{children}</>;
};
